import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiFileText,
  FiPlus,
  FiEye,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiUser,
  FiUsers,
  FiLink,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CardSkeleton, ConfirmDialog } from "@/components/common";

type FactFinderListItem = {
  id: string;
  customerId: string | null;
  accessToken: string | null;
  status: "draft" | "submitted" | "in_review" | "approved" | "archived";
  completedSections: string[];
  lastSectionSaved: string | null;
  createdAt: string;
  updatedAt: string;
  createdByType: "admin" | "customer" | null;
};

const statusConfig: Record<string, { label: string; color: string; icon: typeof FiClock }> = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700", icon: FiClock },
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: FiFileText },
  in_review: { label: "In Review", color: "bg-yellow-100 text-yellow-700", icon: FiAlertCircle },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: FiCheckCircle },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-500", icon: FiFileText },
};

const allSections = [
  "applicant1",
  "applicant2",
  "employment",
  "assets",
  "liabilities",
  "income",
  "expenses",
  "requirements",
];

export function FactFindersPage() {
  const [factFinders, setFactFinders] = useState<FactFinderListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFactFinders = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/fact-finders", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as FactFinderListItem[];
        setFactFinders(data);
      }
    } catch (error) {
      console.error("Failed to fetch fact finders:", error);
      toast.error("Failed to load fact finders");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFactFinders();
  }, [fetchFactFinders]);

  const handleCreate = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch("/api/admin/fact-finders", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail }),
      });

      if (response.ok) {
        toast.success("Fact finder created");
        setIsCreateDialogOpen(false);
        setNewEmail("");
        fetchFactFinders();
      } else {
        toast.error("Failed to create fact finder");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/fact-finders/${deleteId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Fact finder deleted");
        setFactFinders((prev) => prev.filter((ff) => ff.id !== deleteId));
        setDeleteId(null);
      } else {
        toast.error("Failed to delete");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setIsDeleting(false);
    }
  };

  const copyShareableLink = (accessToken: string) => {
    const link = `${window.location.origin}/fact-finder/${accessToken}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const response = await fetch(`/api/admin/fact-finders/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success("Status updated");
        setFactFinders((prev) =>
          prev.map((ff) =>
            ff.id === id ? { ...ff, status: newStatus as FactFinderListItem["status"] } : ff
          )
        );
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("An error occurred");
    }
  };

  const filteredFactFinders = factFinders.filter((ff) => {
    if (statusFilter === "all") return true;
    return ff.status === statusFilter;
  });

  const stats = {
    total: factFinders.length,
    draft: factFinders.filter((ff) => ff.status === "draft").length,
    submitted: factFinders.filter((ff) => ff.status === "submitted").length,
    inReview: factFinders.filter((ff) => ff.status === "in_review").length,
    approved: factFinders.filter((ff) => ff.status === "approved").length,
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Fact Finders</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fact Finders</h1>
          <p className="text-muted-foreground">
            Encrypted customer financial information forms
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
          <FiPlus className="size-4" />
          New Fact Finder
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
            <p className="text-sm text-muted-foreground">Draft</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.submitted}</div>
            <p className="text-sm text-muted-foreground">Submitted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.inReview}</div>
            <p className="text-sm text-muted-foreground">In Review</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <p className="text-sm text-muted-foreground">Approved</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fact Finders List */}
      {filteredFactFinders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FiFileText className="size-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-lg mb-2">No fact finders yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create a new fact finder to collect customer information.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
              <FiPlus className="size-4" />
              New Fact Finder
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFactFinders.map((ff) => {
            const status = statusConfig[ff.status];
            const StatusIcon = status.icon;
            const progress = Math.round(
              (ff.completedSections.length / allSections.length) * 100
            );

            return (
              <Card key={ff.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {ff.createdByType === "admin" ? (
                        <FiUsers className="size-4 text-muted-foreground" />
                      ) : (
                        <FiUser className="size-4 text-muted-foreground" />
                      )}
                      <CardTitle className="text-base">
                        {ff.createdByType === "admin" ? "Admin Created" : "Customer"}
                      </CardTitle>
                    </div>
                    <Badge className={status.color}>
                      <StatusIcon className="size-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {ff.completedSections.length} of {allSections.length} sections completed
                    </p>
                  </div>

                  {/* Last Updated */}
                  <p className="text-xs text-muted-foreground">
                    Updated {new Date(ff.updatedAt).toLocaleDateString()}
                    {ff.lastSectionSaved && ` • Last: ${ff.lastSectionSaved}`}
                  </p>

                  {/* Status Change */}
                  <Select
                    value={ff.status}
                    onValueChange={(value) => handleStatusChange(ff.id, value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button asChild variant="outline" size="sm" className="flex-1">
                      <Link to={`/admin/fact-finders/${ff.id}`}>
                        <FiEye className="size-4 mr-1" />
                        View
                      </Link>
                    </Button>
                    {ff.accessToken && ff.status === "draft" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyShareableLink(ff.accessToken!)}
                        title="Copy shareable link"
                      >
                        <FiLink className="size-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(ff.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <FiTrash2 className="size-4" />
                    </Button>
                  </div>

                  {/* Shareable Link Info */}
                  {ff.accessToken && ff.status === "draft" && (
                    <div className="pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs h-8 text-muted-foreground hover:text-foreground"
                        onClick={() => copyShareableLink(ff.accessToken!)}
                      >
                        <FiLink className="size-3 mr-2" />
                        Copy link to send to customer
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Fact Finder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Create a new fact finder for a phone consultation. Enter the customer's email
              to associate with their account (optional).
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Customer Email (optional)</label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="customer@example.com"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={isCreating}>
                {isCreating ? "Creating..." : "Create Fact Finder"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Fact Finder?"
        description="This will permanently delete this fact finder and all its data. This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
